import { useState, useEffect } from "react";
import { Loader2, Users, Plus } from "lucide-react";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useUpdateAccess, useAccessGroups } from "../hooks/useRepository";
import type { ResourceAccess } from "../types";
import { ALL_ROLES } from "../types";

interface Props {
  resourceId: string;
  access: ResourceAccess;
  /** Full manager (director / owning coordinator) edits roles + individual users.
   *  Otherwise only the access-groups field is shown. */
  canFull: boolean;
  /** Opens the "Grupy dostępu" management modal. */
  onManageGroups?: () => void;
}

export function AccessConfigPanel({ resourceId, access, canFull, onManageGroups }: Props) {
  const { data: allUsers = [] } = useInternalUsers();
  const { data: myGroups = [] } = useAccessGroups();
  const { mutateAsync: updateAccess, isPending } = useUpdateAccess();

  const [roles, setRoles] = useState<string[]>(access.roles);
  const [individualIds, setIndividualIds] = useState<string[]>(access.individual_users);
  const [groupIds, setGroupIds] = useState<string[]>(access.groups.map((g) => g.id));

  useEffect(() => {
    setRoles(access.roles);
    setIndividualIds(access.individual_users);
    setGroupIds(access.groups.map((g) => g.id));
  }, [access.roles, access.individual_users, access.groups]);

  const allRoleValues = ALL_ROLES.map((r) => r.value);
  const allSelected = allRoleValues.every((r) => roles.includes(r));

  function toggleRole(role: string) {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  }

  function toggleAllRoles() {
    setRoles(allSelected ? [] : allRoleValues);
  }

  function toggleUser(id: string) {
    setIndividualIds((prev) =>
      prev.includes(id) ? prev.filter((u) => u !== id) : [...prev, id]
    );
  }

  function toggleGroup(id: string) {
    setGroupIds((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  }

  const internalNonDirectors = allUsers.filter((u) => u.role !== "contractor");

  async function handleSave() {
    if (canFull) {
      await updateAccess({
        id: resourceId,
        data: { roles, individual_users: individualIds, group_ids: groupIds },
      });
    } else {
      await updateAccess({ id: resourceId, data: { group_ids: groupIds } });
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {canFull && (
        <>
          {/* Role checkboxes */}
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Dostęp wg roli
            </p>
            <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
              <input type="checkbox" checked={allSelected} onChange={toggleAllRoles} className="accent-[var(--color-primary)]" />
              <span className="font-medium text-gray-700 dark:text-gray-300">Wszyscy pracownicy</span>
            </label>
            <div className="grid grid-cols-2 gap-1.5 pl-4">
              {ALL_ROLES.map((r) => (
                <label key={r.value} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={roles.includes(r.value)}
                    onChange={() => toggleRole(r.value)}
                    className="accent-[var(--color-primary)]"
                  />
                  <span className="text-gray-700 dark:text-gray-300">{r.label}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
              Dyrektorzy mają zawsze dostęp do wszystkich zasobów.
            </p>
          </div>

          {/* Individual users */}
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Indywidualni użytkownicy
            </p>
            <div className="max-h-48 overflow-y-auto flex flex-col gap-1 p-2">
              {internalNonDirectors.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded px-1 py-0.5">
                  <input
                    type="checkbox"
                    checked={individualIds.includes(u.id)}
                    onChange={() => toggleUser(u.id)}
                    className="accent-[var(--color-primary)]"
                  />
                  <UserAvatar fullName={u.full_name} profilePictureUrl={u.profile_picture_url} size="xs" />
                  <span className="text-gray-700 dark:text-gray-300">{u.full_name}</span>
                  <span className="text-xs text-gray-400 ml-auto">{u.role}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Access groups */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            <Users size={12} /> Grupy dostępu
          </p>
          {onManageGroups && (
            <button
              type="button"
              onClick={onManageGroups}
              className="text-xs text-[var(--color-primary)] dark:text-blue-400 hover:underline flex items-center gap-1"
            >
              <Plus size={11} /> Zarządzaj grupami
            </button>
          )}
        </div>

        {!canFull && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
            Możesz przypisać do tego zasobu własne grupy dostępu.
          </p>
        )}

        {myGroups.length === 0 ? (
          <p className="text-xs text-gray-400 italic px-1">
            Nie masz jeszcze grup dostępu.{" "}
            {onManageGroups && (
              <button type="button" onClick={onManageGroups} className="text-[var(--color-primary)] dark:text-blue-400 hover:underline not-italic">
                Utwórz grupę
              </button>
            )}
          </p>
        ) : (
          <div className="max-h-40 overflow-y-auto flex flex-col gap-1 p-1 border border-gray-100 dark:border-gray-800 rounded-lg">
            {myGroups.map((g) => (
              <label key={g.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded px-1.5 py-1">
                <input
                  type="checkbox"
                  checked={groupIds.includes(g.id)}
                  onChange={() => toggleGroup(g.id)}
                  className="accent-[var(--color-primary)]"
                />
                <span className="text-gray-700 dark:text-gray-300 truncate">{g.name}</span>
                <span className="text-xs text-gray-400 ml-auto">
                  {g.members.length + g.contractors.length} czł.
                </span>
              </label>
            ))}
          </div>
        )}

        {access.other_groups_count > 0 && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
            Przypisano też {access.other_groups_count}{" "}
            {access.other_groups_count === 1 ? "grupę" : "grup"} innych użytkowników.
          </p>
        )}
      </div>

      <button
        onClick={handleSave}
        disabled={isPending}
        className="self-end flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
      >
        {isPending && <Loader2 size={13} className="animate-spin" />}
        Zapisz dostęp
      </button>
    </div>
  );
}
